import { useMemo } from 'react';

import { useSubscriptionPricesQuery } from '@/store/subscriptions/subscriptions.store';

import { billLookupKeys, fmtPrice, periodBill } from './subscription.pricing';
import type { ManagedVesselSubscription } from './useManagedVesselSubscriptions';

/**
 * The vessel's real bill for one period, formatted — or `null` while the prices are in
 * flight and whenever the plan cannot be priced, which is what keeps a made-up figure off
 * the screen.
 *
 * The vessel is its own service, so its id satisfies the `X-Service-Id` header directly.
 * Only the keys the open subscription needs are requested, so a vessel on the free plan
 * makes no request at all.
 */
export const useVesselBill = (
  item: ManagedVesselSubscription | null | undefined,
): string | null => {
  const subscription = item?.subscription ?? null;
  const lookupKeys = useMemo(() => billLookupKeys(subscription), [subscription]);
  const { data: prices } = useSubscriptionPricesQuery(item?.vessel.id, lookupKeys);

  return useMemo(() => {
    if (!item) return null;
    // Nothing to price: the free plan is genuinely zero, not unknown.
    if (lookupKeys.length === 0) return fmtPrice(0, 'usd');

    const bill = periodBill(subscription, prices);
    return bill && fmtPrice(bill.amount, bill.currency);
  }, [item, subscription, lookupKeys, prices]);
};
